'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import ToolSEO from '@/components/ToolSEO';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import Breadcrumbs from '@/components/Breadcrumbs';
import SocialShareButtons from '@/components/SocialShareButtons';
import UniversalBookmark from '@/components/UniversalBookmark';

interface DecisionInputs {
  // Strategic Fit
  coreCompetency: number; // 1-5
  strategicImportance: number; // 1-5
  differentiator: number; // 1-5

  // Cost
  buildCost: number;
  buyCost: number;
  maintenanceYearly: number;

  // Time
  timeToMarketWeeks: number;
  buyTimeToMarketWeeks: number;

  // Technical
  technicalComplexity: number; // 1-5
  teamExpertise: number; // 1-5
  availableSolutions: number; // 1-5

  // Risk
  vendorLockInRisk: number; // 1-5
  securityConcerns: number; // 1-5
  scalabilityNeeds: number; // 1-5

  // Maintenance
  internalMaintenanceCapacity: number; // 1-5
  customizationNeeds: number; // 1-5
}

interface DecisionResult {
  recommendation: 'build' | 'buy' | 'hybrid' | 'undecided';
  confidence: number; // 0-100
  buildScore: number;
  buyScore: number;
  rationale: string[];
  considerations: string[];
  nextSteps: string[];
}

export default function BuildVsBuyMatrixPage() {
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'build-vs-buy-matrix',
    toolCategory: 'analyzer',
  });

  const [inputs, setInputs] = useState<DecisionInputs>({
    coreCompetency: 3,
    strategicImportance: 3,
    differentiator: 3,
    buildCost: 100000,
    buyCost: 50000,
    maintenanceYearly: 20000,
    timeToMarketWeeks: 12,
    buyTimeToMarketWeeks: 4,
    technicalComplexity: 3,
    teamExpertise: 3,
    availableSolutions: 3,
    vendorLockInRisk: 3,
    securityConcerns: 3,
    scalabilityNeeds: 3,
    internalMaintenanceCapacity: 3,
    customizationNeeds: 3,
  });

  const updateInput = <K extends keyof DecisionInputs>(key: K, value: DecisionInputs[K]) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const result = useMemo((): DecisionResult => {
    let buildScore = 0;
    let buyScore = 0;
    const rationale: string[] = [];
    const considerations: string[] = [];
    const nextSteps: string[] = [];

    // Strategic factors (weight: 3x)
    const strategicScore = (inputs.coreCompetency + inputs.strategicImportance + inputs.differentiator) / 3;
    if (strategicScore >= 4) {
      buildScore += 30;
      rationale.push('High strategic value strongly favors building internally');
    } else if (strategicScore >= 3) {
      buildScore += 15;
      buyScore += 15;
    } else {
      buyScore += 30;
      rationale.push('Low strategic value favors buying existing solution');
    }

    // Cost analysis (weight: 2.5x)
    const fiveYearBuildCost = inputs.buildCost + (inputs.maintenanceYearly * 5);
    const fiveYearBuyCost = inputs.buyCost + (inputs.maintenanceYearly * 0.5 * 5); // Assume buy costs less to maintain
    if (fiveYearBuildCost < fiveYearBuyCost * 0.7) {
      buildScore += 25;
      rationale.push(`Building is significantly more cost-effective (5-year TCO: $${fiveYearBuildCost.toLocaleString()} vs $${fiveYearBuyCost.toLocaleString()})`);
    } else if (fiveYearBuildCost < fiveYearBuyCost) {
      buildScore += 10;
      buyScore += 10;
    } else {
      buyScore += 25;
      rationale.push(`Buying is more cost-effective (5-year TCO: $${fiveYearBuyCost.toLocaleString()} vs $${fiveYearBuildCost.toLocaleString()})`);
    }

    // Time to market (weight: 2x)
    if (inputs.timeToMarketWeeks <= inputs.buyTimeToMarketWeeks * 1.5) {
      buildScore += 15;
    } else if (inputs.timeToMarketWeeks > inputs.buyTimeToMarketWeeks * 2) {
      buyScore += 20;
      rationale.push(`Significantly faster time to market with buy option (${inputs.buyTimeToMarketWeeks} vs ${inputs.timeToMarketWeeks} weeks)`);
    } else {
      buyScore += 10;
    }

    // Technical factors (weight: 2x)
    if (inputs.teamExpertise >= 4 && inputs.technicalComplexity <= 3) {
      buildScore += 20;
      rationale.push('Strong team expertise and manageable complexity favor building');
    } else if (inputs.teamExpertise <= 2 || inputs.technicalComplexity >= 4) {
      buyScore += 20;
      rationale.push('Limited expertise or high complexity favor buying');
    }

    // Available solutions (weight: 1.5x)
    if (inputs.availableSolutions >= 4) {
      buyScore += 15;
      rationale.push('Mature market with good solutions available');
    } else if (inputs.availableSolutions <= 2) {
      buildScore += 15;
      rationale.push('Few quality solutions available in market');
    }

    // Customization needs (weight: 2x)
    if (inputs.customizationNeeds >= 4) {
      buildScore += 20;
      rationale.push('High customization needs favor building');
    } else if (inputs.customizationNeeds <= 2) {
      buyScore += 20;
    }

    // Vendor lock-in risk (weight: 1.5x)
    if (inputs.vendorLockInRisk >= 4) {
      buildScore += 15;
      considerations.push('High vendor lock-in risk with buy option');
    } else if (inputs.vendorLockInRisk <= 2) {
      buyScore += 10;
    }

    // Security concerns (weight: 2x)
    if (inputs.securityConcerns >= 4) {
      buildScore += 20;
      considerations.push('Security concerns favor internal development');
    }

    // Scalability needs (weight: 1.5x)
    if (inputs.scalabilityNeeds >= 4 && inputs.availableSolutions <= 3) {
      buildScore += 15;
      considerations.push('High scalability needs may not be met by existing solutions');
    }

    // Maintenance capacity (weight: 1.5x)
    if (inputs.internalMaintenanceCapacity >= 4) {
      buildScore += 10;
    } else if (inputs.internalMaintenanceCapacity <= 2) {
      buyScore += 15;
      considerations.push('Limited maintenance capacity favors managed solution');
    }

    // Normalize scores to 0-100
    const maxScore = 100;
    buildScore = Math.min(buildScore, maxScore);
    buyScore = Math.min(buyScore, maxScore);

    // Determine recommendation
    const scoreDiff = Math.abs(buildScore - buyScore);
    let recommendation: DecisionResult['recommendation'];
    let confidence: number;

    if (scoreDiff < 10) {
      recommendation = 'hybrid';
      confidence = 40;
      nextSteps.push('Consider a hybrid approach: Buy for MVP, plan to build later');
      nextSteps.push('Evaluate build vs. buy for different components');
    } else if (scoreDiff < 20) {
      recommendation = buildScore > buyScore ? 'build' : 'buy';
      confidence = 60;
      nextSteps.push('Gather more detailed requirements and vendor proposals');
      nextSteps.push('Create prototype or proof of concept');
    } else {
      recommendation = buildScore > buyScore ? 'build' : 'buy';
      confidence = 85;
    }

    if (buildScore < 30 && buyScore < 30) {
      recommendation = 'undecided';
      confidence = 30;
      nextSteps.push('Need more information to make informed decision');
      nextSteps.push('Re-evaluate strategic importance and requirements');
    }

    // Add general next steps based on recommendation
    if (recommendation === 'build') {
      nextSteps.push('Create detailed technical specification');
      nextSteps.push('Estimate team allocation and timeline');
      nextSteps.push('Identify key technical risks and mitigation strategies');
      nextSteps.push('Plan for long-term maintenance and support');
    } else if (recommendation === 'buy') {
      nextSteps.push('Create vendor evaluation criteria');
      nextSteps.push('Request demos and proposals from top vendors');
      nextSteps.push('Negotiate contract terms and SLAs');
      nextSteps.push('Plan integration and migration strategy');
    } else if (recommendation === 'hybrid') {
      nextSteps.push('Identify which components to buy vs. build');
      nextSteps.push('Create integration architecture');
      nextSteps.push('Plan transition from buy to build if applicable');
    }

    // Add considerations if not enough rationale
    if (rationale.length < 3) {
      if (inputs.differentiator >= 4) {
        considerations.push('High differentiation value - consider building for competitive advantage');
      }
      if (inputs.teamExpertise >= 4) {
        considerations.push('Strong team expertise available for internal development');
      }
      if (inputs.availableSolutions >= 4) {
        considerations.push('Mature vendor ecosystem with proven solutions');
      }
    }

    return {
      recommendation,
      confidence,
      buildScore,
      buyScore,
      rationale,
      considerations,
      nextSteps,
    };
  }, [inputs]);

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const gaps = result.considerations.map((consideration, idx) => ({
      id: `consideration-${idx}`,
      title: consideration,
      severity: 'medium' as const,
      description: consideration,
    }));

    const recommendations = result.nextSteps.map((step, idx) => ({
      id: `step-${idx}`,
      title: step,
      priority: idx < 2 ? 'high' as const : 'medium' as const,
      description: step,
      effort: 'Medium',
    }));

    const assessment = await saveResult({
      name: `Build vs Buy Analysis - ${new Date().toLocaleDateString()}`,
      score_overall: result.confidence,
      scores: {
        buildScore: result.buildScore,
        buyScore: result.buyScore,
        confidence: result.confidence,
      },
      answers: inputs as unknown as Record<string, unknown>,
      gaps,
      recommendations,
      metadata: {
        recommendation: result.recommendation,
        rationale: result.rationale,
        fiveYearBuildCost: inputs.buildCost + (inputs.maintenanceYearly * 5),
        fiveYearBuyCost: (inputs.buyCost * 5) + (inputs.maintenanceYearly * 0.5 * 5),
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  const exportAsADR = () => {
    const date = new Date().toISOString().split('T')[0];
    const fiveYearBuild = inputs.buildCost + (inputs.maintenanceYearly * 5);
    const fiveYearBuy = (inputs.buyCost * 5) + (inputs.maintenanceYearly * 0.5 * 5);

    const md = [
      `# ADR: Build vs Buy Decision`,
      ``,
      `**Date**: ${date}`,
      `**Status**: Proposed`,
      `**Decision**: ${result.recommendation === 'build' ? 'Build Internally' : result.recommendation === 'buy' ? 'Buy / Subscribe' : result.recommendation === 'hybrid' ? 'Hybrid Approach' : 'Undecided'}`,
      `**Confidence**: ${result.confidence}%`,
      ``,
      `## Context`,
      ``,
      `A build vs buy analysis was conducted to determine the optimal approach for this capability.`,
      ``,
      `- Build Score: ${result.buildScore}/100`,
      `- Buy Score: ${result.buyScore}/100`,
      `- 5-Year Build Cost: $${fiveYearBuild.toLocaleString()}`,
      `- 5-Year Buy Cost: $${fiveYearBuy.toLocaleString()}`,
      ``,
      `## Decision`,
      ``,
      ...result.rationale.map(r => `- ${r}`),
      ``,
      `## Considerations`,
      ``,
      ...result.considerations.map(c => `- ${c}`),
      ``,
      `## Consequences`,
      ``,
      `### Next Steps`,
      ...result.nextSteps.map(s => `- ${s}`),
      ``,
      `---`,
      `*Generated by The Art of CTO Build vs Buy Decision Matrix*`,
    ].join('\n');

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adr-build-vs-buy-${date}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const recommendationColors = {
    'build': 'border-blue-500 bg-blue-500/10',
    'buy': 'border-green-500 bg-green-500/10',
    'hybrid': 'border-yellow-500 bg-yellow-500/10',
    'undecided': 'border-border bg-background/50',
  };

  const recommendationIcons = {
    'build': '🔨',
    'buy': '🛒',
    'hybrid': '🔀',
    'undecided': '🤔',
  };

  const recommendationLabels = {
    'build': 'Build Internally',
    'buy': 'Buy/Subscribe',
    'hybrid': 'Hybrid Approach',
    'undecided': 'Need More Info',
  };

  const TOOL_NAME = 'Build vs Buy Decision Matrix';
  const TOOL_SLUG = 'build-vs-buy-matrix';
  const TOOL_DESCRIPTION = 'Make informed decisions on whether to build, buy, or take a hybrid approach';

  return (
    <>
      <ToolSEO
        toolName={TOOL_NAME}
        toolSlug={TOOL_SLUG}
        description={TOOL_DESCRIPTION}
        category="analyzer"
      />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <Breadcrumbs
            items={[
              { name: 'Tools', url: '/tools' },
              { name: TOOL_NAME, url: `/tools/${TOOL_SLUG}` },
            ]}
          />

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">⚖️</span>
                  <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                    {TOOL_NAME}
                  </h1>
                </div>
                <p className="text-xl text-foreground-secondary">
                  {TOOL_DESCRIPTION}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <UniversalBookmark
                  contentType="tool"
                  contentSlug={TOOL_SLUG}
                  contentTitle={TOOL_NAME}
                  variant="icon"
                />
                <SocialShareButtons
                  url={`https://theartofcto.com/tools/${TOOL_SLUG}`}
                  title={TOOL_NAME}
                  description={TOOL_DESCRIPTION}
                  variant="dropdown"
                />
              </div>
            </div>
          </div>

        {/* Instructions */}
        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this tool</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Rate strategic factors (core competency, importance, differentiation)</li>
            <li>Enter cost and time estimates for both options</li>
            <li>Assess technical factors and team capabilities</li>
            <li>Review the recommendation and detailed analysis</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Inputs */}
          <div className="space-y-6">
            {/* Strategic Factors */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Strategic Factors</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Core Competency: {inputs.coreCompetency}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.coreCompetency}
                    onChange={(e) => updateInput('coreCompetency', parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs text-foreground-tertiary mt-1">
                    Is this a core competency for your business?
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Strategic Importance: {inputs.strategicImportance}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.strategicImportance}
                    onChange={(e) => updateInput('strategicImportance', parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs text-foreground-tertiary mt-1">
                    How strategically important is this capability?
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Competitive Differentiator: {inputs.differentiator}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.differentiator}
                    onChange={(e) => updateInput('differentiator', parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs text-foreground-tertiary mt-1">
                    Will this differentiate you from competitors?
                  </div>
                </div>
              </div>
            </div>

            {/* Cost & Time */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Cost & Time</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Estimated Build Cost
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    min="0"
                    step="10000"
                    value={inputs.buildCost}
                    onChange={(e) => updateInput('buildCost', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Estimated Buy Cost (Annual)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    min="0"
                    step="10000"
                    value={inputs.buyCost}
                    onChange={(e) => updateInput('buyCost', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Annual Maintenance Cost (Build)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    min="0"
                    step="5000"
                    value={inputs.maintenanceYearly}
                    onChange={(e) => updateInput('maintenanceYearly', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Build Time to Market (Weeks)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    min="1"
                    value={inputs.timeToMarketWeeks}
                    onChange={(e) => updateInput('timeToMarketWeeks', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Buy Time to Market (Weeks)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    min="1"
                    value={inputs.buyTimeToMarketWeeks}
                    onChange={(e) => updateInput('buyTimeToMarketWeeks', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                  />
                </div>
              </div>
            </div>

            {/* Technical Factors */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Technical Factors</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Technical Complexity: {inputs.technicalComplexity}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.technicalComplexity}
                    onChange={(e) => updateInput('technicalComplexity', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Team Expertise: {inputs.teamExpertise}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.teamExpertise}
                    onChange={(e) => updateInput('teamExpertise', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Available Solutions Quality: {inputs.availableSolutions}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.availableSolutions}
                    onChange={(e) => updateInput('availableSolutions', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Customization Needs: {inputs.customizationNeeds}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.customizationNeeds}
                    onChange={(e) => updateInput('customizationNeeds', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Risk Factors */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Risk & Operational Factors</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Vendor Lock-in Risk: {inputs.vendorLockInRisk}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.vendorLockInRisk}
                    onChange={(e) => updateInput('vendorLockInRisk', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Security Concerns: {inputs.securityConcerns}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.securityConcerns}
                    onChange={(e) => updateInput('securityConcerns', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Scalability Needs: {inputs.scalabilityNeeds}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.scalabilityNeeds}
                    onChange={(e) => updateInput('scalabilityNeeds', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Internal Maintenance Capacity: {inputs.internalMaintenanceCapacity}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={inputs.internalMaintenanceCapacity}
                    onChange={(e) => updateInput('internalMaintenanceCapacity', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Results */}
          <div className="space-y-6">
            {/* Recommendation */}
            <div className={`bg-surface rounded-lg border ${recommendationColors[result.recommendation]} p-6`}>
              <h3 className="text-xl font-bold text-foreground mb-4">Recommendation</h3>

              <div className="text-center py-6 mb-4">
                <div className="text-6xl mb-4">{recommendationIcons[result.recommendation]}</div>
                <div className="text-3xl font-bold text-foreground mb-2">
                  {recommendationLabels[result.recommendation]}
                </div>
                <div className="text-lg text-accent">
                  {result.confidence}% confidence
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Build Score</div>
                  <div className="text-3xl font-bold text-blue-400">{result.buildScore}</div>
                </div>
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Buy Score</div>
                  <div className="text-3xl font-bold text-green-400">{result.buyScore}</div>
                </div>
              </div>
            </div>

            {/* Rationale */}
            {result.rationale.length > 0 && (
              <div className="bg-surface rounded-lg border border-border p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">Key Factors</h3>
                <ul className="space-y-2">
                  {result.rationale.map((reason, i) => (
                    <li key={i} className="flex items-start gap-2 text-foreground-secondary text-sm">
                      <span className="text-accent mt-1">•</span>
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Considerations */}
            {result.considerations.length > 0 && (
              <div className="bg-surface rounded-lg border border-border p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">Additional Considerations</h3>
                <ul className="space-y-2">
                  {result.considerations.map((consideration, i) => (
                    <li key={i} className="flex items-start gap-2 text-foreground-secondary text-sm">
                      <span className="text-yellow-500 mt-1">⚠</span>
                      <span>{consideration}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* TCO Comparison */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">5-Year TCO Comparison</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-foreground-secondary">Build</span>
                  <span className="text-blue-400 font-bold text-lg">
                    {formatCurrency(inputs.buildCost + (inputs.maintenanceYearly * 5))}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-secondary">Buy</span>
                  <span className="text-green-400 font-bold text-lg">
                    {formatCurrency((inputs.buyCost * 5) + (inputs.maintenanceYearly * 0.5 * 5))}
                  </span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-accent-muted border border-border rounded-lg p-6">
              <h3 className="text-lg font-bold text-accent mb-3">Next Steps</h3>
              <ol className="space-y-2 list-decimal list-inside">
                {result.nextSteps.map((step, i) => (
                  <li key={i} className="text-foreground-secondary text-sm">{step}</li>
                ))}
              </ol>
            </div>

            {/* Save to Command Center */}
            {commandCenterEnabled && !saved && (
              <button
                onClick={handleSaveToCommandCenter}
                disabled={saving}
                className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save to Command Center'}
              </button>
            )}
            {saved && (
              <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved to Command Center
              </div>
            )}
            {saveError && (
              <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm text-center">
                {saveError}
              </div>
            )}

            {/* Export as ADR */}
            <button
              onClick={exportAsADR}
              className="w-full px-6 py-3 border border-border text-foreground font-semibold rounded-lg hover:border-border-muted0 hover:text-accent transition-colors"
            >
              Export as Architecture Decision Record
            </button>
          </div>
        </div>

        {/* Decision Matrix Guide */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Decision Framework</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-foreground mb-3">Consider Building When:</h4>
              <ul className="space-y-2 text-foreground-secondary text-sm">
                <li>• Core to your business strategy</li>
                <li>• Strong competitive differentiator</li>
                <li>• Team has deep expertise</li>
                <li>• High customization needs</li>
                <li>• No suitable solutions exist</li>
                <li>• Long-term cost savings</li>
                <li>• High security/compliance requirements</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-3">Consider Buying When:</h4>
              <ul className="space-y-2 text-foreground-secondary text-sm">
                <li>• Not a core competency</li>
                <li>• Mature market with good options</li>
                <li>• Fast time to market needed</li>
                <li>• Limited internal resources</li>
                <li>• Standard functionality sufficient</li>
                <li>• Lower total cost of ownership</li>
                <li>• Vendor expertise exceeds internal</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-3">Related Resources</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              href="/frameworks"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Technical Decision Frameworks →</h4>
              <p className="text-sm text-foreground-tertiary">
                Structured approaches to make-or-buy decisions
              </p>
            </Link>
            <Link
              href="/tools/architecture-calculator"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Architecture Calculator →</h4>
              <p className="text-sm text-foreground-tertiary">
                Evaluate different technical approaches
              </p>
            </Link>
          </div>
        </div>
        </div>
      </div>
    </>
  );
}
